import React, { useEffect, useRef } from "react";
import { X } from "lucide-react";

/**
 * Modal
 *
 * Generic modal wrapper — closes on overlay click, Escape key, or the X button.
 *
 * Props:
 *   isOpen: boolean        - whether the modal is visible
 *   onClose: () => void    - called when the modal should close
 *   title?: string         - optional header title
 *   children: ReactNode    - modal body content
 *
 * Usage:
 *   {showKPIPopup === "Daily Sales" && (
 *     <Modal isOpen={true} onClose={() => setShowKPIPopup(null)} title="Daily Sales">
 *       <DailySales />
 *     </Modal>
 *   )}
 */
const Modal = ({ isOpen, onClose, title, children }) => {
  const overlayRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    // prevent background scroll while modal is open
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) onClose();
  };

  return (
    <div
      ref={overlayRef}
      onClick={handleOverlayClick}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15, 23, 42, 0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 2000,
        padding: 16,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        style={{
          background: "#fff",
          borderRadius: 14,
          width: "100%",
          maxWidth: 1200,
          maxHeight: "85vh",
          overflowY: "auto",
          boxShadow: "0 20px 60px rgba(15, 23, 42, 0.25)",
          animation: "modal-pop 0.18s ease",
        }}
      >
        {title && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "16px 20px",
              borderBottom: "1px solid #e2e8f0",
              position: "sticky",
              top: 0,
              background: "#fff",
              borderRadius: "14px 14px 0 0",
            }}
          >
            <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a" }}>
              {/* {title}  */}

              <h3 className="text-sm font-bold text-slate-900 uppercase tracking-widest flex items-center gap-2">
                  <span className="w-1.5 h-5 bg-gradient-to-b from-rose-500 to-pink-500 rounded-full" />
                  {title} 
                </h3>
            </div>
            <button
              onClick={onClose}
              aria-label="Close"
              style={{
                width: 30,
                height: 30,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
                borderRadius: 8,
                cursor: "pointer",
              }}
            >
              <X size={15} color="#475569" strokeWidth={2} />
            </button>
          </div>
        )}
        <div style={{ padding: 20 }}>{children}</div>
        {/* <div style={{ padding: 0 }}>{children}</div> */}
      </div>

      <style>{`
        @keyframes modal-pop {
          from { opacity: 0; transform: scale(0.96); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
};

export default Modal;